import PropTypes from 'prop-types';
import React from 'react';
import ReactDOM from 'react-dom';
import Browser from '@wetransfer/concorde-browser';

import { position } from '@app/helper/tooltip-menu';
export default class Tooltip extends React.Component {
  static propTypes = {
    className: PropTypes.string,
    content: PropTypes.any.isRequired,
    anchor: PropTypes.string,
    offset: PropTypes.number,
    trigger: PropTypes.oneOf(['hover', 'click', 'manual']),
    children: PropTypes.object,
    visible: PropTypes.bool,
    autoHide: PropTypes.bool,
    dynamicPointer: PropTypes.bool,
  };

  static defaultProps = {
    anchor: 'top',
    offset: 0,
    trigger: 'hover',
    visible: false,
    autoHide: true,
    dynamicPointer: false,
  };

  state = {
    content: this.props.content,
    anchor: this.props.anchor,
    hidden: true,
  };

  constructor(props) {
    super(props);

    this.rootRef = React.createRef();

    this.hide = this.hide.bind(this);
    this.positionTooltip = this.positionTooltip.bind(this);
    this.show = this.show.bind(this);
  }

  componentDidMount() {
    // Always anchor the tooltip at the bottom if the given anchor is left or right
    if (Browser.isMobile && ['left', 'right'].indexOf(this.props.anchor) > -1) {
      this.setState({ anchor: 'bottom' });
    }

    this._isMounted = true; // @see https://facebook.github.io/react/blog/2015/12/16/ismounted-antipattern.html
    this.createTooltip();
    this.renderContent();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.hidden !== this.state.hidden && !this.state.hidden) {
      this.renderContent();
    }

    if (prevProps.visible !== this.props.visible) {
      if (this.props.visible) {
        this.show();
      } else {
        this.hide();
      }
    }
  }

  componentWillUnmount() {
    this.removeListeners();

    this.hide(() => {
      this._isMounted = false;
      ReactDOM.unmountComponentAtNode(this.tooltip);
    });
  }

  renderContent() {
    ReactDOM.render(this.props.content, this.tooltip, () => {
      // Create and position popup, and add all listeners we need
      const { className, trigger } = this.props;
      const { anchor } = this.state;

      const classNames = ['tooltip'];
      if (trigger) {
        classNames.push('tooltip--' + trigger);
      }
      if (anchor) {
        classNames.push('tooltip--' + anchor);
      }
      if (className) {
        classNames.push(className);
      }

      this.tooltip.className = classNames.join(' ');

      if (this.state.hidden === false) {
        this.positionTooltip();
      }
    });

    this.addListeners();
  }

  createTooltip() {
    this.tooltip = document.createElement('div');
  }

  positionTooltip() {
    if (!this._isMounted) return;

    // Position the tooltip based on the properties of the first children.
    // We don't want to use the properties of `this.rootRef.current` as this
    // is just a wrapper around the children and might break the layout.
    // The layout and the position of the tooltip should come from children.
    const firstChildRef = this.rootRef.current.children.item(0);

    position(this.tooltip, firstChildRef, this.props.offset, this.state.anchor);
  }

  addListeners() {
    const { trigger } = this.props;
    const element = this.rootRef.current;

    if (!element) return;

    if (trigger === 'hover') {
      element.addEventListener('mouseover', this.show, false);
      element.addEventListener('mouseleave', this.hide, false);
    }

    document.addEventListener('blur', this.handleOutsideClick, true);
    window.addEventListener('scroll', this.positionTooltip, true);
  }

  removeListeners() {
    const element = this.rootRef.current;

    if (!element) return;

    element.removeEventListener('mouseover', this.show);
    element.removeEventListener('mouseleave', this.hide);
    document.removeEventListener('blur', this.handleOutsideClick);
    window.removeEventListener('scroll', this.positionTooltip);
  }

  show(e = false) {
    if (!this.state.hidden) {
      return;
    }

    this.setState({ hidden: false });
    document.body.appendChild(this.tooltip);
    this.positionTooltip();

    // Oh no, The poptip doesn't fit in the browser! Let's quickly hide it, reposition it to the bottom and show it again.
    // TODO: Make it smarter to always rotate to the next anchor. (since this only happens on the profile page now, it's fine.) & Move it back when it fits again after resize? (edge case)
    const newState = this.isOutsideViewport();
    if (newState) {
      this.adaptToViewport(e, newState);
      return;
    }
  }

  adaptToViewport(e, newState) {
    this.hide(() => {
      this.setState(newState, () => {
        this.renderContent();
        this.show(e);
      });
    });
  }

  isOutsideViewport() {
    // Outside viewport from right
    if (
      window.innerWidth -
        (this.tooltip.offsetWidth + this.tooltip.getBoundingClientRect().left) <
      0
    ) {
      if (Browser.isMobile || this.state.anchor === 'bottom') {
        return { anchor: 'bottom-left' };
      } else if (this.state.anchor === 'left') {
        return { anchor: 'bottom' };
      }
    }

    // Outside viewport from bottom
    if (
      this.state.anchor === 'bottom' &&
      window.innerHeight <
        this.tooltip.offsetHeight + this.tooltip.getBoundingClientRect().top
    ) {
      return { anchor: 'top' };
    }

    // Outside viewport from top
    if (
      this.state.anchor === 'top' &&
      this.tooltip.getBoundingClientRect().top < 0
    ) {
      return { anchor: 'bottom' };
    }

    return false;
  }

  hide(callback) {
    const cleanup = () => {
      if (!this.state.hidden) {
        document.body.removeChild(this.tooltip);
      }
      this.setState({ hidden: true });
      if (typeof callback === 'function') {
        callback();
      }
    };
    cleanup();
  }

  render() {
    return (
      <div className="poptip" ref={this.rootRef}>
        {this.props.children}
      </div>
    );
  }
}
